import { useState, useEffect } from 'react';
import {
  createResource,getResources,
  getResourceById
} from '../../../router/api';
import toast from 'react-hot-toast';

interface CustomerDetailProps {
  customer: any;
  onBack: () => void;
  onEdit: (customer: any) => void;
}

interface SaleHistoryItem {
  date: string;
  reference: string;
  montant: number;
  statut: string;
}

interface InvoiceItem {
  numero: string;
  date: string;
  montant_ttc: number;
  montant_restant: number;
  statut: string;
  echeance: string;
}

interface OrderItem {
  numero: string;
  date: string;
  montant: number;
  statut: string;
}

interface PaymentItem {
  date: string;
  reference: string;
  montant: number;
  mode: string;
}

export default function CustomerDetail({ customer, onBack, onEdit }: CustomerDetailProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'invoices' | 'orders' | 'payments'>('info');

  const [salesHistory, setSalesHistory] = useState<SaleHistoryItem[]>([]);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [payments, setPayments] = useState<PaymentItem[]>([]);

  const [loadingTab, setLoadingTab] = useState(false);

  const availableCredit = customer.plafond_credit - customer.encours_actuel;
  const creditUsagePercent = customer.plafond_credit > 0 
    ? (customer.encours_actuel / customer.plafond_credit) * 100 
    : 0;

  const formatGNF = (value: number) => {
    return value.toLocaleString('fr-GN', { minimumFractionDigits: 0 }) + ' GNF';
  };

  const getStatusColor = () => {
    if (customer.bloque) return 'bg-red-100 text-red-800';
    if (!customer.actif) return 'bg-gray-100 text-gray-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusLabel = () => {
    if (customer.bloque) return 'Bloqué';
    if (!customer.actif) return 'Inactif';
    return 'Actif';
  };

  const getTypeClientLabel = () => {
    const labels: Record<string, string> = {
      PARTICULIER: 'Particulier',
      PROFESSIONNEL: 'Professionnel',
      GROSSISTE: 'Grossiste',
      SEMI_GROSSISTE: 'Semi-Grossiste'
    };
    return labels[customer.type_client] || customer.type_client;
  };

  const getRisqueCreditColor = () => {
    const colors: Record<string, string> = {
      FAIBLE: 'text-green-600',
      MOYEN: 'text-orange-600',
      ELEVE: 'text-red-600',
      CRITIQUE: 'text-red-800 font-bold'
    };
    return colors[customer.risque_credit] || 'text-gray-600';
  };

  const getDelaiPaiementLabel = () => {
    if (customer.delai_paiement_jours === 0) return 'Comptant';
    if (customer.delai_paiement_jours === 30) return '30 Jours Net';
    if (customer.delai_paiement_jours === 45) return '45 Jours Net';
    if (customer.delai_paiement_jours === 60) return '60 Jours Net';
    return `${customer.delai_paiement_jours} Jours`;
  };

  // Chargement des données selon l'onglet actif
  useEffect(() => {
    const loadTabData = async () => {
      if (activeTab === 'info') return;

      setLoadingTab(true);
      try {
        let response;
        switch (activeTab) {
          case 'history':
            response = await getResources(`clients/${customer.id}/sales-history`);
            setSalesHistory(response.data.data || []);
            break;
          case 'invoices':
            response = await getResources(`clients/${customer.id}/invoices`);
            setInvoices(response.data.data || []);
            break;
          case 'orders':
            response = await getResources(`clients/${customer.id}/orders`);
            setOrders(response.data.data || []);
            break;
          case 'payments':
            response = await getResources(`clients/${customer.id}/payments`);
            setPayments(response.data.data || []);
            break;
        }
      } catch (err) {
        toast.error(`Erreur lors du chargement des ${activeTab === 'history' ? 'ventes' : activeTab}`);
      } finally {
        setLoadingTab(false);
      }
    };

    loadTabData();
  }, [activeTab, customer.id]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900">
          <i className="ri-arrow-left-line mr-2"></i>
          Retour à la liste
        </button>
        <button
          onClick={() => onEdit(customer)}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <i className="ri-edit-line"></i>
          Modifier
        </button>
      </div>

      {/* Informations Principales */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{customer.raison_sociale}</h2>
            <p className="text-sm text-gray-500 mt-1">Référence: {customer.code}</p>
            {customer.nom_commercial && <p className="text-sm text-gray-600 italic">{customer.nom_commercial}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
              {getStatusLabel()}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {getTypeClientLabel()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Informations Légales</h3>
            <div className="space-y-1">
              <p className="text-sm text-gray-900"><strong>NIF:</strong> {customer.nif || 'Non renseigné'}</p>
              <p className="text-sm text-gray-900"><strong>Registre:</strong> {customer.registre_commerce || '—'}</p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Contact Principal</h3>
            <div className="space-y-1">
              <p className="text-sm text-gray-900">{customer.contact || 'Non renseigné'}</p>
              {customer.fonction_contact && <p className="text-sm text-gray-500">{customer.fonction_contact}</p>}
              <p className="text-sm text-gray-900">{customer.email || '—'}</p>
              {customer.telephone && <p className="text-sm text-gray-900">{customer.telephone}</p>}
              {customer.whatsapp && <p className="text-sm text-gray-900">WhatsApp: {customer.whatsapp}</p>}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Commercial Attribué</h3>
            <p className="text-sm text-gray-900">{customer.commercial?.name || 'Non assigné'}</p>
          </div>
        </div>
      </div>

      {/* Encours et Crédit */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Plafond de Crédit</h3>
            <i className="ri-money-euro-circle-line text-2xl text-blue-600"></i>
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatGNF(customer.plafond_credit)}</p>
          <p className={`text-sm mt-2 font-medium ${getRisqueCreditColor()}`}>
            Risque: {customer.risque_credit}
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Encours Actuel</h3>
            <i className="ri-wallet-line text-2xl text-orange-600"></i>
          </div>
          <p className="text-3xl font-bold text-orange-600">{formatGNF(customer.encours_actuel)}</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  creditUsagePercent > 90 ? 'bg-red-600' :
                  creditUsagePercent > 70 ? 'bg-orange-600' :
                  'bg-green-600'
                }`}
                style={{ width: `${Math.min(creditUsagePercent, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{creditUsagePercent.toFixed(1)}% utilisé</p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Crédit Disponible</h3>
            <i className="ri-check-line text-2xl text-green-600"></i>
          </div>
          <p className="text-3xl font-bold text-green-600">{formatGNF(availableCredit)}</p>
          <p className="text-sm text-gray-500 mt-2">
            Délai: {getDelaiPaiementLabel()}
          </p>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'info'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-information-line mr-2"></i>
              Informations Complètes
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              Historique des Ventes
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'invoices'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Créances en Cours
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'orders'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              Commandes
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === 'payments'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-money-euro-circle-line mr-2"></i>
              Règlements
            </button>
          </nav>
        </div>

        <div className="p-6">
          {loadingTab && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-500">Chargement...</p>
            </div>
          )}

          {/* Informations Complètes */}
          {!loadingTab && activeTab === 'info' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <i className="ri-building-line mr-2 text-blue-600"></i>
                  Adresse de Facturation
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {customer.adresse_facturation ? (
                    <>
                      <p className="text-sm text-gray-900">{customer.adresse_facturation}</p>
                      {customer.quartier && <p className="text-sm text-gray-900">{customer.quartier}</p>}
                      <p className="text-sm text-gray-900">{customer.ville}, {customer.pays}</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Non renseignée</p>
                  )}
                </div>
              </div>

              {/* Adresses de livraison si stockées en JSON */}
              {customer.adresse_livraison && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <i className="ri-truck-line mr-2 text-blue-600"></i>
                    Adresses de Livraison
                  </h3>
                  {(() => {
                    try {
                      const addresses = JSON.parse(customer.adresse_livraison);
                      return Array.isArray(addresses) && addresses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {addresses.map((addr: any, i: number) => (
                            <div key={i} className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-gray-900">{addr.label}</span>
                                {addr.isDefault && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    Par défaut
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {addr.address}<br />
                                {addr.quartier && `${addr.quartier}, `}{addr.ville}, {addr.pays}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Aucune adresse enregistrée</p>
                      );
                    } catch {
                      return <p className="text-sm text-gray-500">Format invalide</p>;
                    }
                  })()}
                </div>
              )}

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <i className="ri-price-tag-3-line mr-2 text-blue-600"></i>
                  Conditions Commerciales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Délai de Paiement</p>
                    <p className="text-lg font-medium text-gray-900">{getDelaiPaiementLabel()}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Risque Crédit</p>
                    <p className={`text-lg font-medium ${getRisqueCreditColor()}`}>
                      {customer.risque_credit}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Historique des Ventes */}
          {!loadingTab && activeTab === 'history' && (
            salesHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {salesHistory.map((sale, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm">{sale.date}</td>
                        <td className="px-4 py-3 text-sm">{sale.reference}</td>
                        <td className="px-4 py-3 text-sm font-medium">{formatGNF(sale.montant)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            sale.statut === 'payée' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {sale.statut}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <i className="ri-history-line text-4xl mb-4"></i>
                <p>Aucune vente enregistrée</p>
              </div>
            )
          )}

          {/* Créances en cours */}
          {!loadingTab && activeTab === 'invoices' && (
            invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Facture</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Échéance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant TTC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restant Dû</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoices.map((inv, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm">{inv.numero}</td>
                        <td className="px-4 py-3 text-sm">{inv.date}</td>
                        <td className="px-4 py-3 text-sm">{inv.echeance}</td>
                        <td className="px-4 py-3 text-sm font-medium">{formatGNF(inv.montant_ttc)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-orange-600">{formatGNF(inv.montant_restant)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            inv.statut === 'payée' ? 'bg-green-100 text-green-800' :
                            inv.statut === 'en retard' ? 'bg-red-100 text-red-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {inv.statut}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <i className="ri-file-text-line text-4xl mb-4"></i>
                <p>Aucune facture en cours</p>
              </div>
            )
          )}

          {/* Commandes */}
          {!loadingTab && activeTab === 'orders' && (
            orders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Commande</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orders.map((order, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm">{order.numero}</td>
                        <td className="px-4 py-3 text-sm">{order.date}</td>
                        <td className="px-4 py-3 text-sm font-medium">{formatGNF(order.montant)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            order.statut === 'livrée' ? 'bg-green-100 text-green-800' :
                            order.statut === 'en cours' ? 'bg-blue-100 text-blue-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {order.statut}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <i className="ri-shopping-cart-line text-4xl mb-4"></i>
                <p>Aucune commande enregistrée</p>
              </div>
            )
          )}

          {/* Règlements */}
          {!loadingTab && activeTab === 'payments' && (
            payments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payments.map((pay, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm">{pay.date}</td>
                        <td className="px-4 py-3 text-sm">{pay.reference}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">{formatGNF(pay.montant)}</td>
                        <td className="px-4 py-3 text-sm">{pay.mode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <i className="ri-money-euro-circle-line text-4xl mb-4"></i>
                <p>Aucun règlement enregistré</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}